import { useEffect } from 'react';
import { handleOAuthSignIn, pollForGoogle } from './auth.utils';

const GOOGLE_CLIENT_IDS = {
  'si.com':
    '463477417652-hutbmku64c94svf03itqrno88ljmmpsc.apps.googleusercontent.com',
  'mentalfloss.com': '714792666811-lk0d9knqbktb95rt17kbdoc4074bmn85.apps.googleusercontent.com',
  // FanSided and FanSided network id
  default:
    '102936792104-jftdt1qtikefs9ac4barqnbt1g6b9f5i.apps.googleusercontent.com',
} as const;

const getClientId = (url: string): string => {
  if (url.includes('si.com')) {
    return GOOGLE_CLIENT_IDS['si.com'];
  }
  if (url.includes('www.mentalfloss.com')) {
    return GOOGLE_CLIENT_IDS['mentalfloss.com'];
  }
  return GOOGLE_CLIENT_IDS.default;
};

declare global {
  interface Window {
    isGoogleOAuthInitialized?: boolean;
    isGoogleOAuthPrompted?: boolean;
    googleOAuthPromptTimeoutId?: number;
  }
}

// Schedule prompt 15s after the first user scroll
const schedulePromptAfterDelay = () => {
  if (!window.isGoogleOAuthInitialized) return;
  if (window.isGoogleOAuthPrompted || window.googleOAuthPromptTimeoutId) return;

  const promptEvent = new CustomEvent('google-oauth-modal', {
    detail: {
      name: 'prompt',
    },
  });
  const closeEvent = new CustomEvent('google-oauth-modal', {
    detail: {
      name: 'closed',
    },
  });

  window.googleOAuthPromptTimeoutId = window.setTimeout(async () => {
    try {
      window.google.accounts.id.prompt(() => {
        // callback is only called when the modal is closed
        window.dispatchEvent(closeEvent);
      });
      window.isGoogleOAuthPrompted = true;
      window.dispatchEvent(promptEvent);
    } catch (e) {
      console.error('Failed to prompt Google OAuth:', e);
    }
  }, 15000);
};

interface GoogleOAuthProps {
  propertyUrl?: string;
  experiments?: string[];
  isMobile?: boolean;
}

export const GoogleOAuth = ({ propertyUrl, experiments = [], isMobile = false }: GoogleOAuthProps) => {
  useEffect(() => {
    if (!propertyUrl || document.cookie.includes('mm_auth_session')) {
      return;
    }

    const hasScrollExperiment = experiments?.some(experiment => experiment.includes('scroll'));

    const initializeGoogle = async () => {
      try {
        await pollForGoogle();

        window.google.accounts.id.initialize({
          client_id: getClientId(propertyUrl),
          callback: (response: any) => handleOAuthSignIn('google', response),
          auto_select: false,
          cancel_on_tap_outside: false,
          itp_support: true,
        });
        window.isGoogleOAuthInitialized = true;

        // TODO: remove after this experiment ends
        // control group should not prompt on desktop
        // if (!isMobile || !hasScrollExperiment) {
        //   window.google.accounts.id.prompt();
        // }
      } catch (error) {
        console.error('Failed to initialize Google OAuth:', error);
      }
    };

    if (isMobile && hasScrollExperiment) {
      if (!window.isGoogleOAuthInitialized) {
        initializeGoogle();
      }
      // One-time listener for the first scroll event
      window.addEventListener('scroll', schedulePromptAfterDelay, {
        once: true,
        passive: true,
      });
    }
  }, [propertyUrl, experiments, isMobile]);

  return null;
};
